import React from 'react';
import PropTypes from 'prop-types';

import { Logger } from 'aws-amplify';

import { 
  makeStyles,
} from '@material-ui/core/styles';

import { 
  Avatar,
  Card,
  CardHeader,
  CardContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';

import {
  FlashOn as ExecutionIcon,
  MoreVert as MoreIcon,
  Repeat as RepeatIcon,
  Stop as StopIcon,
} from '@material-ui/icons';

import { 
  ComponentGrouper,
  DetailPanel,
  FieldValue,
} from "../../../packages/detail-panel";

import { 
  ResourceRef,
} from '../../resource';

import { 
  useWorkloadDataConfiguration,
} from '../../workload';

import { 
  fmtDataConfigurationRefs 
} from '../../workload/formatters';

import { 
  fmtIso8601AsLocalDateTime,
} from '../../utils/formatters';

import {
  fmtExecutionDuration,
  fmtExecutionObjectIOPerMin,
  fmtExecutionBytesOutputPerMin,
} from '../formatters';

import {
  ExecutionActionRepeat,
  ExecutionActionStop,
  ExecutionAlert,
  ExecutionInputs,
  ExecutionInputFeedStatus,
  ExecutionOverrides,
  ExecutionResult,
  ExecutionResults,
  ExecutionStatus,
  ExecutionTimings,
} from ".";

const logger = new Logger('ExecutionCard');

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  sections: {
    padding: theme.spacing(0),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'row',
  },
  section: {
    padding: theme.spacing(1),
    minWidth: 300,
    flexGrow: 1,
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
}));

const fmtTasks = (tasks) => {
  if(tasks) {
    let active=(tasks.Started>=0 && tasks.Stopped>=0)?tasks.Started-tasks.Stopped:0;
    return `${tasks.Started}${active?` [${active}]`:''}`;
  }
  return(0);
};

export default function ExecutionCard(props) {
  const { execution, showInputs } = props;

  const classes = useStyles();

  const [anchorEl, setAnchorEl] = React.useState(null);

  const [openRepeatExecution, setOpenRepeatExecution] = React.useState(false);
  const [openStopExecution, setOpenStopExecution] = React.useState(false);

  const [resultFilter, setResultFilter] = React.useState(showInputs && showInputs!='ALL'?showInputs:null);
  const [expandExecutionInputs, doExpandExecutionInputs] = React.useState(0);

  const { resource: workload } = useWorkloadDataConfiguration(execution?execution.WorkloadId:null);

  if(!workload) return(null); 
  if(!execution) return(null); 

  execution['Workload'] = workload;
  
  logger.debug("ENRICHED EXECUTION:",execution);

  const handleActionMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleActionMenuClose = () => {
    setAnchorEl(null);
  };
  const handleOpenRepeatExecution = () => {
    handleActionMenuClose();
    setOpenRepeatExecution(true);
  };
  const handleCloseRepeatExecution = () => {
    setOpenRepeatExecution(false);
  };
  const handleOpenStopExecution = () => {
    handleActionMenuClose();
    setOpenStopExecution(true);
  };
  const handleCloseStopExecution = () => {
    setOpenStopExecution(false);
  };

  const handleSelectResultType = (type) => {
    setResultFilter(type);
    doExpandExecutionInputs(e => e+1);
  };

  const fields=[
    { section: "Details", fields: [
      { name: "StartTime", label: "Started", formatter: fmtIso8601AsLocalDateTime },
      { name: "EndTime", label: "Completed", formatter: fmtIso8601AsLocalDateTime },
      { name: "Duration", formatter: fmtExecutionDuration, data: "$" },
    ]},
    { section: "Configuration", fields: [
      { name: "Configuration.ImageTag", label: "Image" },
      { name: "Configuration.Memory", label: "Memory" },
      { name: "Overrides", component: ExecutionOverrides, valueProp: 'execution', data: "$" },
    ]},
    { section: "Data", fields: [
      { name: "Workload.DataConfiguration.SourceDatasets", label: "Sources", formatter: fmtDataConfigurationRefs,
        component: ResourceRef, options: { type: 'Dataset', format: 'qualified', adorned: false } },
      { name: "Workload.DataConfiguration.TargetDataset", label: "Target", formatter: fmtDataConfigurationRefs, 
        component: ResourceRef, options: { type: 'Dataset', format: 'qualified', adorned: false } },
      { name: "Feed", label: "Input Feed", component: ExecutionInputFeedStatus, data: "$" },
    ]},
    { section: "Performance", fields: [
      { name: "Summary", label: "_", component: ComponentGrouper, options: {
          components: [
            {
              component: ExecutionTimings,
              props: {
                value: execution,
                variant: 'graph',
              }
            },
            {
              component: DetailPanel,
              props: {
                data: execution,
                fields: [
                  { name: "Objects (io/m)", formatter: fmtExecutionObjectIOPerMin, data: "$" },
                  { name: "Bytes (up/m)", formatter: fmtExecutionBytesOutputPerMin, data: "$" },
                  { name: "Tasks", label: "Tasks", formatter: fmtTasks },
                ]
              }
            },
          ]}, data: "$" },
      { name: "TimingsTable", label: "_", component: ExecutionTimings, options: { variant: 'table' }, data: "$" },
    ]},
    { section: "Status & Results", fields: [
      { name: "Summary", label: "_", component: ComponentGrouper, options: { 
          components: [
            {
              component: ExecutionResults,
              sm: 4,
              props: {
                value: execution,
                variant: 'graph',
                onSelectResultType: handleSelectResultType,
              }
            },
            {
              component: DetailPanel,
              sm: 6,
              props: {
                data: execution,
                fields: [
                  { name: "Status", component: ExecutionStatus, valueProp: 'execution', options: { adornment: 'end' }, data: '$' },
                  { name: "Result", component: ExecutionResult, options: { adornment: 'end' } },
                ]
              }
            },
          ]}, data: "$" },
      { name: "ResultTable", label: "_", component: ExecutionResults, options: { variant: 'table', onSelectResultType: handleSelectResultType }, data: "$" },
    ]},
    { section: "Related", expandable: true, expanded: true, fields: [ 
      { name: "ClientAccountId", label: "Client", component: ResourceRef, 
        options: { type: 'Client', format: 'qualified', adorned: true } },
      { name: "Workload", label: "Workload", component: ResourceRef, 
        options: { type: 'Workload', format: 'qualified', adorned: true } },
    ]},
    { section: "Inputs", 
      expandable: true, expanded: false || (typeof showInputs !== 'undefined'), expand: expandExecutionInputs,
      footer: { 
        component: ExecutionInputs,
        props: { execution: execution.ExecutionId, withResult: resultFilter, title: null },
      }
    },
  ];

  const sections=[];
  fields.forEach(field => {
    if(field.section) {
      sections.push(field);
    }
  });

  return (
    <Card className={classes.root} variant="outlined">
      <CardHeader
        avatar={
          <Avatar aria-label="source-data" className={classes.avatar}>
            <ExecutionIcon/>
          </Avatar>
        }
        action={
          <IconButton aria-label="execution-action-menu" onClick={handleActionMenuOpen}>
            <MoreIcon />
          </IconButton>
        }
        title={<FieldValue value={execution.ExecutionId}/>} 
        subheader={execution.Description?execution.Description:"Execution"}
      />
      <CardContent>
        <ExecutionAlert execution={execution}/>
        <Grid container className={classes.sections}>
          {
            sections.map((section, index) => {
              return(
                <Grid key={"s"+index} item className={classes.section} >
                  <DetailPanel
                    name={section.section} 
                    data={execution}
                    {...section}
                  />
                </Grid>
              );
            })
          }
        </Grid>
      </CardContent>
      <Menu
        id="action-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleActionMenuClose}
      >
        <MenuItem disabled={execution.Status!=='COMPLETE'} onClick={handleOpenRepeatExecution}><RepeatIcon/>&nbsp;Repeat Execution...</MenuItem>
        <MenuItem disabled={execution.Status==='COMPLETE'} onClick={handleOpenStopExecution}><StopIcon/>&nbsp;Stop Execution...</MenuItem>
      </Menu>
      {openRepeatExecution?<ExecutionActionRepeat execution={execution} open={openRepeatExecution} onClose={handleCloseRepeatExecution}/>:null}
      {openStopExecution?<ExecutionActionStop execution={execution} open={openStopExecution} onClose={handleCloseStopExecution}/>:null}
    </Card>
  );
}

ExecutionCard.propTypes = {
  execution: PropTypes.object.isRequired,
  showInputs: PropTypes.oneOf(['ALL','REJECTED','SKIPPED','FAILED','SUCCEEDED']),
};

ExecutionCard.defaultProps = {
};



